// Per-patient PDF export, ported from exportPatientPDF (source lines
// 8275-8419): a generic dumper over every visit/section/field, not a curated
// clinical layout - matches source behavior. Uses the jspdf npm package
// (bundled by esbuild) instead of the ~250KB minified jsPDF the source
// embedded inline in the HTML.
import { jsPDF } from "jspdf";
import { patientsApi, studiesApi, visitsApi, mhApi, cmApi, aeApi } from "./api-client.js";
import { fmtDate, fmtDateOnly } from "./dom.js";
import { STATE } from "./state.js";
import { VISIT_CONFIG } from "@woof/shared";

export async function exportPatientPDF(pid: string): Promise<void> {
  const patient = await patientsApi.get(pid);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = margin;

  function newPageIfNeeded(needed: number) {
    if (y + needed > 280) {
      doc.addPage();
      y = margin;
    }
  }
  function header(text: string) {
    newPageIfNeeded(13);
    doc.setFillColor(30, 58, 138);
    doc.setTextColor(255);
    doc.setFontSize(11);
    doc.rect(margin, y, pageW - 2 * margin, 7, "F");
    doc.text(text, margin + 2, y + 5);
    doc.setTextColor(0);
    // Extra breathing room below the title bar before the section's content
    // starts, on top of the bar's own height.
    y += 9 + 4;
  }
  function line(label: string, val: unknown) {
    newPageIfNeeded(5);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(`${label}:`, margin, y);
    doc.setFont("helvetica", "normal");
    const txt = val === null || val === undefined || val === "" ? "—" : String(val);
    const wrapped = doc.splitTextToSize(txt, pageW - 2 * margin - 50);
    doc.text(wrapped, margin + 50, y);
    y += Math.max(5, wrapped.length * 4);
  }

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("WOOF-01 — eCRF Patient Report", margin, y);
  y += 8;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Protocole F1901CT2024-01 v2.0 — Findimmune × CHUV VetAgroSup", margin, y);
  y += 5;
  doc.text(`Export généré le ${fmtDate(new Date().toISOString())} par ${STATE.user?.username ?? "?"}`, margin, y);
  y += 10;

  header("Identification patient");
  line("ID", patient.id);
  line("Site", patient.siteid);
  line("Initiales", patient.initials);
  line("Race", patient.breed);
  line("DOB", fmtDateOnly(patient.brthdtc));
  line("Sexe", patient.sex);
  line("Type tumoral", patient.tumorType);
  line("Statut", patient.status);
  line("Notes", patient.notes);

  if (patient.studyId) {
    const study = await studiesApi.get(patient.studyId);
    header("Étude clinique");
    line("Nom", study.name);
    line("Espèce", study.species === "human" ? "Humain" : "Animal");
    line("Description", study.description);
    line("Sites investigateurs", (study.siteCodes || []).join(", "));
  }

  for (const visit of VISIT_CONFIG) {
    if (visit.virtual) continue;
    const { sections } = await visitsApi.get(pid, visit.vk);
    if (Object.keys(sections).length === 0) continue;
    header(`${visit.label} (${visit.day})`);
    for (const [sectionId, sectionData] of Object.entries(sections)) {
      if (!sectionData || typeof sectionData !== "object") continue;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      newPageIfNeeded(6);
      doc.text(`• ${sectionId}`, margin, y);
      y += 5;
      doc.setFont("helvetica", "normal");
      for (const [key, val] of Object.entries(sectionData as Record<string, unknown>)) {
        if (val === "" || val === null || val === undefined) continue;
        line(`  ${key}`, typeof val === "object" ? JSON.stringify(val) : val);
      }
    }
  }

  const mh = await mhApi.list(pid);
  if (mh.length) {
    header("Antécédents médicaux");
    mh.forEach((m: any) => line(m.mhterm, `${m.treatment || ""} (${fmtDateOnly(m.mhstdat)} - ${m.ongoing ? "en cours" : fmtDateOnly(m.mhendat)})`));
  }
  const cm = await cmApi.list(pid);
  if (cm.length) {
    header("Traitements concomitants");
    cm.forEach((c: any) => line(c.cmtrt, `${c.cmdose || ""} ${c.cmroute} — ${c.cmindc || ""}`));
  }
  const aes = await aeApi.list(pid);
  if (aes.length) {
    header("Événements indésirables");
    aes.forEach((ae: any) => {
      line(ae.aeterm, `Grade ${ae.aetoxgr} — ${ae.aeser ? "SAE" : "Non sérieux"} — ${ae.aeout}`);
      if (ae.sae) {
        line("  Critères SAE", (ae.sae.criteria || []).join(", "));
        line("  Chronologie", ae.sae.chronology);
      }
    });
  }

  doc.save(`WOOF1_${pid}_export_${new Date().toISOString().slice(0, 10)}.pdf`);
}
